import { useCallback, useEffect, useRef, useState } from "react";
import { api, Device, SnapshotView } from "./api";

/**
 * Miniatura sob demanda por card visível: nunca pré-assina a frota inteira. A URL é
 * temporária, o cache respeita a expiração e no máximo três cards buscam ao mesmo tempo.
 */
const previewCache = new Map<string, SnapshotView>();
const maximumConcurrentLoads = 3;
let activeLoads = 0;
const waiting: Array<() => void> = [];

function withLoadSlot<T>(task: () => Promise<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const run = () => {
      activeLoads++;
      task()
        .then(resolve, reject)
        .finally(() => {
          activeLoads--;
          waiting.shift()?.();
        });
    };
    if (activeLoads < maximumConcurrentLoads) run();
    else waiting.push(run);
  });
}

type PreviewState = "idle" | "loading" | "empty" | "error" | "expired";

/** Remove URLs já vencidas para o cache não crescer com cards que saíram da tela. */
function pruneCache() {
  const now = Date.now();
  for (const [id, cached] of previewCache) {
    if (Date.parse(cached.expiresAt) <= now) previewCache.delete(id);
  }
}

function DeviceCard({ device }: { device: Device }) {
  const [view, setView] = useState<SnapshotView>();
  const [state, setState] = useState<PreviewState>("idle");
  const [error, setError] = useState("");
  const [visible, setVisible] = useState(false);
  const card = useRef<HTMLElement>(null);

  useEffect(() => {
    const element = card.current;
    if (!element || typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        setVisible(true);
        observer.disconnect();
      },
      { rootMargin: "200px" },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!view) return;
    const remaining = Date.parse(view.expiresAt) - Date.now();
    if (remaining <= 0) {
      previewCache.delete(device.id);
      setView(undefined);
      setState("expired");
      return;
    }
    const timer = window.setTimeout(() => {
      previewCache.delete(device.id);
      setView(undefined);
      setState("expired");
    }, remaining);
    return () => window.clearTimeout(timer);
  }, [view, device.id]);

  const load = useCallback(async (force: boolean) => {
    pruneCache();
    const cached = previewCache.get(device.id);
    if (!force && cached && Date.parse(cached.expiresAt) - Date.now() > 5_000) {
      setView(cached);
      setState("idle");
      return;
    }
    setState("loading");
    setError("");
    try {
      const end = new Date();
      const page = await withLoadSlot(() =>
        api.timeline(device.id, new Date(end.getTime() - 7 * 86400000).toISOString(), end.toISOString()),
      );
      if (!page.items[0]) {
        setState("empty");
        return;
      }
      const loaded = await withLoadSlot(() => api.snapshotView(device.id, page.items[0].id));
      previewCache.set(device.id, loaded);
      setView(loaded);
      setState("idle");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Não foi possível carregar a captura.");
      setState("error");
    }
  }, [device.id]);

  useEffect(() => {
    if (!visible) return;
    void load(false);
  }, [visible, load]);

  return <article className="device-card" ref={card}>
    <div className="panel-title"><a href={`#/devices/${device.id}`}><strong>{device.machineName}</strong></a><span className={`status ${device.status}`}>{device.status === "online" ? "Online" : "Offline"}</span></div>
    {device.tags?.length ? <div className="tag-list">{device.tags.map((tag) => <span className="tag-chip" key={tag}>{tag}</span>)}</div> : null}
    {view
      ? <><img className="device-preview" src={view.downloadUrl} alt={`Última captura disponível de ${device.machineName}`} onError={() => { previewCache.delete(device.id); setView(undefined); setState("error"); setError("A captura expirou ou não pôde ser carregada. Atualize a miniatura."); }} /><small>Capturada em {new Date(view.capturedAt).toLocaleString("pt-BR")}</small></>
      : <div className="device-preview-placeholder">{
          state === "loading" ? "Carregando última captura…" :
          state === "empty" ? "Sem capturas nos últimos sete dias." :
          state === "expired" ? "A miniatura expirou. Atualize para carregar novamente." :
          state === "error" ? "Não foi possível carregar a última captura." :
          "Captura carregada somente quando solicitada"
        }</div>}
    <p className="muted">Último contato: {device.lastSeenAt ? new Date(device.lastSeenAt).toLocaleString("pt-BR") : "Nunca"}</p>
    <p className="muted">{device.planName} · {device.screenRetentionDays} dias de telas</p>
    {state === "error" && error && <p role="alert" className="error">{error}</p>}
    <div className="actions"><button disabled={state === "loading"} onClick={() => void load(true)}>{state === "loading" ? "Abrindo…" : "Atualizar miniatura"}</button><a href={`#/devices/${device.id}/timeline`}>Histórico DVR</a></div>
  </article>;
}

export function DeviceMosaic({ devices }: { devices: Device[] }) {
  return <div className="device-mosaic">{devices.map(device => <DeviceCard key={device.id} device={device} />)}</div>;
}
